import {
  Box,
  Button,
  Chip,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Schema } from '../../../../../../amplify/data/resource';
import TextFieldUnderline from '../../../../../components/custom/TextFieldUnderline';

interface WhatsAppDialogProps {
  templateId: string;
  setTemplateId: (id: string) => void;
}

interface FormValues {
  title: string;
  text: string;
}

const apiClient = generateClient<Schema>();

const TemplateEdit: React.FC<WhatsAppDialogProps> = ({
  templateId,
  setTemplateId,
}) => {
  const [variables, setVariables] = useState<string[]>([]);

  const {
    handleSubmit,
    control,
    setValue,
    setError,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      title: '',
      text: '',
    },
  });

  const textValue = watch('text', '');

  const extractVariables = (text: string) => {
    const matches = text.match(/{{\d+}}/g) || [];
    return Array.from(new Set(matches));
  };

  useEffect(() => {
    const extractedVariables = extractVariables(textValue);
    if (extractedVariables.length > 5) {
      setError('text', {
        type: 'manual',
        message: 'No more than 5 variables are allowed',
      });
    } else {
      setVariables(extractedVariables);
    }
  }, [textValue, setError]);

  useEffect(() => {
    const fetchTemplate = async () => {
      if (templateId !== 'create') {
        try {
          const response = await apiClient.models.WhatsAppTemplate.get({
            id: templateId,
          });

          if (response?.data) {
            setValue('title', response.data.title || '');
            setValue('text', response.data.text || '');
          } else {
            console.error('Template data is null or undefined');
          }
        } catch (error) {
          console.error('Failed to fetch template:', error);
        }
      }
    };

    fetchTemplate();
  }, [templateId, setValue]);

  const onSubmit = async (formData: FormValues) => {
    try {
      if (templateId === 'create') {
        await apiClient.models.WhatsAppTemplate.create({
          title: formData.title,
          text: formData.text,
        });
        setTemplateId('');
      } else {
        await apiClient.models.WhatsAppTemplate.update({
          id: templateId,
          title: formData.title,
          text: formData.text,
        });
        setTemplateId('');
      }

      console.log('Template successfully submitted:', formData);
    } catch (error) {
      console.error('Failed to submit template:', error);
    }
  };

  return (
    <DialogContent
      sx={{
        display: 'flex',
        flexDirection: 'column',
        pt: '70px',
        px: '100px',
        backgroundColor: 'transparent',
        flex: 1,
        overflowY: 'auto',
        boxSizing: 'border-box',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Stack
        component="form"
        direction="row"
        justifyContent={'space-between'}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Stack
          spacing="40px"
          sx={{
            background: 'transparent',
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#E9DDD3',
              width: '400px',
              height: '300px',
              borderRadius: '15px',
            }}
          ></Box>
          <Button
            type="submit"
            sx={{
              backgroundColor: '#B6F829',
              borderRadius: '15px',
              ':hover': { backgroundColor: '#B6F829' },
            }}
          >
            <Typography
              fontSize="32px"
              fontWeight="700"
              color="black"
              fontFamily={'AlmoniDL'}
            >
              הוסף הודעה
            </Typography>
          </Button>
        </Stack>
        <Stack spacing={5}>
          <Typography fontSize="60px" fontWeight="300" fontFamily={'AlmoniDL'}>
            הוסף הודעה חדשה
          </Typography>
          <Stack spacing={20}>
            <Controller
              name="title"
              control={control}
              rules={{ required: 'חובה למלא את השדה' }}
              render={({ field }) => (
                <TextFieldUnderline
                  {...field}
                  label="שם ההודעה"
                  fullWidth
                  required
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />
            <Stack spacing={1}>
              <Controller
                name="text"
                control={control}
                rules={{ required: 'חובה למלא את השדה' }}
                render={({ field }) => (
                  <TextFieldUnderline
                    {...field}
                    label="תוכן ההודעה"
                    fullWidth
                    multiline
                    required
                    error={!!errors.text}
                    helperText={errors.text?.message}
                  />
                )}
              />
              {variables.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {variables.map((variable, index) => (
                    <Chip
                      key={index}
                      label={variable}
                      variant="outlined"
                      sx={{
                        fontSize: '15px',
                        fontWeight: '700',
                        fontFamily: 'AlmoniDL',
                        backgroundColor: 'transparent',
                      }}
                    />
                  ))}
                </Box>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </DialogContent>
  );
};

export default TemplateEdit;
